import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, createTextVNode as _createTextVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["aria-expanded"]
const _hoisted_2 = { class: "completion-menu__item-content" }
const _hoisted_3 = {
  key: 0,
  class: "completion-menu__badge completion-menu__badge--highlight"
}
const _hoisted_4 = { class: "completion-menu__badge-text" }
const _hoisted_5 = {
  key: 1,
  class: "completion-menu__add-highlight"
}
const _hoisted_6 = { class: "completion-menu__item-content" }
const _hoisted_7 = { class: "completion-menu__badge-text" }

import { computed, onBeforeMount, ref } from 'vue'
import { useRouter } from 'vue-router'

import { Mixpanel } from '@/analytics/mixpanel'
import type { CompletionFlowAction } from '@/analytics/mixpanel/mixpanel'
import type { CompletionPopupData } from '@/models/completion'
import { DocExportView } from '@/models/doc.export'
import { assertNotNull } from '@/helpers/typing'
import {
  DOCS_USER_DATA_CACHE,
  DOC_KEYS,
  HOME_CACHE,
  resetCacheByKeys,
  updateDocsUserDataCache,
} from '@/helpers/cache'
import { auth } from '@/services/auth'
import { backend } from '@/services/backend'
import { playerSession } from '@/services/player/session'
import { platform } from '@/services/platform'
import { apiURL } from '@/services/request'
import { appNotifications } from '@/services/notification.app'

import ActionMenu from '@/app/components/ActionMenu.vue'
import Action from '@/app/components/ActionMenuItem.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'CompletionMenu',
  props: /*@__PURE__*/_mergeModels({
    data: {}
  }, {
    "modelValue": { type: Boolean, ...{ default: false } },
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["dismiss"], ["update:modelValue"]),
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const open = _useModel<boolean>(__props, "modelValue")
const router = useRouter()
const isAndroid = ref(false)
const saved = ref(props.data.is_favorite)

const completedExerciseCount = computed(
  () => props.data.exercises.filter((exercise) => exercise.completed).length,
)
const allExercisesCompleted = computed(
  () => completedExerciseCount.value === props.data.exercises.length,
)
const exportView = computed(
  () => new DocExportView(props.data.book, assertNotNull(auth.getUser())),
)
const canDownload = computed(() => exportView.value.canDownload())
const downloadUrl = computed(
  () => `${apiURL}pdfdocs?token=${props.data.book.doc_export_stats.token}`,
)
const downloadName = computed(
  () => `Shortform Summary - ${props.data.book.title.replace('/', '-')}.pdf`,
)

onBeforeMount(async () => {
  isAndroid.value = await platform.isNativeAndroidApp()
})

function close(): void {
  open.value = false
}

function dismiss(): void {
  close()
  emit('dismiss')
}

function dismissAndOpen(route: Parameters<typeof router.push>[0]): void {
  dismiss()
  void router.push(route)
}

function trackAction(action: CompletionFlowAction): void {
  Mixpanel.trackCompletionFlowAction(props.data.book.id, action)
}

function openHighlights(): void {
  trackAction('open-highlights')
  dismissAndOpen({
    name: 'book.highlights',
    params: { url_slug: props.data.book.url_slug },
  })
}

function openExercises(): void {
  trackAction('open-exercises')
  dismissAndOpen({
    name: 'book.page',
    params: {
      url_slug: props.data.book.url_slug,
      page_url_slug: props.data.exercises[0].url_slug,
    },
  })
}

async function save(): Promise<void> {
  trackAction('save')
  close()
  if (saved.value) {
    return
  }

  await backend.favorite({
    id: props.data.book.id,
    title: props.data.book.title,
    doc_type: 'book',
    is_free: props.data.book.is_free,
    summary_words: props.data.book.summary_words,
  })
  saved.value = true
  appNotifications.success({ title: 'Saved to Favorites' })
  await resetCacheByKeys([HOME_CACHE, DOCS_USER_DATA_CACHE])
}

function download(event: MouseEvent): void {
  trackAction('download')
  close()
  if (isAndroid.value) {
    event.preventDefault()
    void platform.openUrl(downloadUrl.value)
  }
}

function openExport(): void {
  trackAction('download')
  dismissAndOpen({
    name: 'book.export',
    params: { url_slug: props.data.book.url_slug },
  })
}

function openDiscussion(): void {
  trackAction('discuss')
  dismissAndOpen({
    name: 'book.community',
    params: { url_slug: props.data.book.url_slug },
  })
}

async function resetProgress(): Promise<void> {
  trackAction('mark-as-not-started')
  close()
  // The popup can appear over the guide that just finished playing. Stop its
  // audio session so replaying it will use the reset chapter progress.
  if (playerSession.value.doc?.id === props.data.book.id) {
    await playerSession.value.stop()
  }
  const userData = await backend.resetBookProgress(props.data.book.id)
  await Promise.all([
    updateDocsUserDataCache(props.data.book.id, userData),
    resetCacheByKeys(DOC_KEYS.book),
  ])
  dismissAndOpen({
    name: 'book.page',
    // Navigation within this guide normally reuses the reader. Ask it to load
    // fresh data so it no longer shows the progress we have just reset.
    query: { reload: '1' },
    params: {
      url_slug: props.data.book.url_slug,
      page_url_slug: '',
    },
  })
}

function buyOnAmazon(): void {
  trackAction('buy-on-amazon')
  close()
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (open.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "completion-menu-backdrop",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (open.value = false))
        }))
      : _createCommentVNode("", true),
    _createVNode(ActionMenu, {
      modelValue: open.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((open).value = $event)),
      class: "completion-menu",
      position: "center",
      "menu-id": "completion-actions"
    }, {
      button: _withCtx(() => [
        _createElementVNode("button", {
          class: "completion-menu__toggle",
          type: "button",
          "aria-label": "Guide actions",
          "aria-controls": "completion-actions",
          "aria-expanded": open.value
        }, [...(_cache[2] || (_cache[2] = [
          _createElementVNode("span", { class: "iconfont imore" }, null, -1)
        ]))], 8, _hoisted_1)
      ]),
      default: _withCtx(() => [
        _createVNode(Action, {
          onClick: _withModifiers(openHighlights, ["prevent"])
        }, {
          default: _withCtx(() => [
            _cache[4] || (_cache[4] = _createElementVNode("span", { class: "completion-menu__icon completion-menu__icon--highlights" }, null, -1)),
            _createElementVNode("span", _hoisted_2, [
              _cache[3] || (_cache[3] = _createElementVNode("span", null, "Highlights", -1)),
              (__props.data.highlights.count)
                ? (_openBlock(), _createElementBlock("span", _hoisted_3, [
                    _createElementVNode("span", _hoisted_4, _toDisplayString(__props.data.highlights.count), 1)
                  ]))
                : (_openBlock(), _createElementBlock("span", _hoisted_5, " Add highlight "))
            ])
          ]),
          _: 1
        }),
        (__props.data.exercises.length)
          ? (_openBlock(), _createBlock(Action, {
              key: 0,
              onClick: _withModifiers(openExercises, ["prevent"])
            }, {
              default: _withCtx(() => [
                _cache[6] || (_cache[6] = _createElementVNode("span", { class: "completion-menu__icon completion-menu__icon--exercises" }, null, -1)),
                _createElementVNode("span", _hoisted_6, [
                  _cache[5] || (_cache[5] = _createElementVNode("span", null, "Exercises", -1)),
                  _createElementVNode("span", {
                    class: _normalizeClass(["completion-menu__badge", {
            'completion-menu__badge--complete': allExercisesCompleted.value,
          }])
                  }, [
                    _createElementVNode("span", _hoisted_7, _toDisplayString(completedExerciseCount.value) + "/" + _toDisplayString(__props.data.exercises.length), 1)
                  ], 2)
                ])
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createVNode(Action, {
          onClick: _withModifiers(save, ["prevent"])
        }, {
          default: _withCtx(() => [
            _createElementVNode("span", {
              class: _normalizeClass(["completion-menu__icon completion-menu__icon--save", { 'completion-menu__icon--saved': saved.value }])
            }, null, 2),
            _cache[7] || (_cache[7] = _createTextVNode(" Save ", -1))
          ]),
          _: 1
        }),
        (canDownload.value)
          ? (_openBlock(), _createBlock(Action, {
              key: 1,
              href: downloadUrl.value,
              download: downloadName.value,
              onClick: download
            }, {
              default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                _createElementVNode("span", { class: "completion-menu__icon completion-menu__icon--download" }, null, -1),
                _createTextVNode(" Download ", -1)
              ]))]),
              _: 1
            }, 8, ["href", "download"]))
          : (_openBlock(), _createBlock(Action, {
              key: 2,
              onClick: _withModifiers(openExport, ["prevent"])
            }, {
              default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                _createElementVNode("span", { class: "completion-menu__icon completion-menu__icon--download" }, null, -1),
                _createTextVNode(" Download ", -1)
              ]))]),
              _: 1
            })),
        (__props.data.book.amazon_link && !isAndroid.value)
          ? (_openBlock(), _createBlock(Action, {
              key: 3,
              href: __props.data.book.amazon_link,
              onClick: buyOnAmazon
            }, {
              default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                _createElementVNode("span", { class: "completion-menu__icon completion-menu__icon--buy" }, null, -1),
                _createTextVNode(" Buy on Amazon ", -1)
              ]))]),
              _: 1
            }, 8, ["href"]))
          : _createCommentVNode("", true),
        _createVNode(Action, {
          onClick: _withModifiers(openDiscussion, ["prevent"])
        }, {
          default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
            _createElementVNode("span", { class: "completion-menu__icon completion-menu__icon--discuss" }, null, -1),
            _createTextVNode(" Discuss ", -1)
          ]))]),
          _: 1
        }),
        _createVNode(Action, {
          onClick: _withModifiers(resetProgress, ["prevent"])
        }, {
          default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
            _createElementVNode("span", { class: "completion-menu__icon completion-menu__icon--reset" }, null, -1),
            _createTextVNode(" Mark as not started ", -1)
          ]))]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"])
  ], 64))
}
}

})